import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const sectionTitleStyle = {
  fontSize: { xs: '1.8rem', md: '2.8rem' },
  fontWeight: 800,
  lineHeight: 1.15,
  color: '#f8fafc',
};

const bulletStyle = {
  color: '#e2e8f0',
  fontSize: { xs: '1rem', md: '1.2rem' },
  lineHeight: 1.4,
  fontWeight: 500,
  mb: 1.2,
};

const panelBase = {
  borderRadius: '16px',
  border: '1px solid',
  p: { xs: 2.5, md: 3.5 },
  minHeight: { xs: 'auto', md: 330 },
};

const wideBannerSx = {
  width: '100%',
  height: 'auto',
  aspectRatio: '16 / 9',
  objectFit: 'cover',
  display: 'block',
};

const sheen = {
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.18) 50%, transparent 75%)',
    transform: 'translateX(-130%)',
    opacity: 0,
    transition: 'transform 850ms ease, opacity 350ms ease',
    pointerEvents: 'none',
  },
  '&:hover::after': {
    transform: 'translateX(130%)',
    opacity: 0.32,
  },
};

function ResilientLogo({ alt, sources, size = 54 }) {
  const [index, setIndex] = React.useState(0);
  const current = sources[index];

  if (!current) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          border: '1px solid rgba(148,163,184,0.6)',
          color: '#e2e8f0',
          fontWeight: 800,
          fontSize: 14,
        }}
      >
        {alt.slice(0, 2).toUpperCase()}
      </Box>
    );
  }

  return (
    <img
      src={current}
      alt={alt}
      onError={() => setIndex((prev) => prev + 1)}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
}

function UsbInstallerRoadmap() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 5 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 12% 10%, rgba(34,211,238,0.18), transparent 35%), radial-gradient(circle at 85% 12%, rgba(168,85,247,0.22), transparent 34%), linear-gradient(180deg, #020617 0%, #0b1530 100%)',
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ResilientLogo
              alt="Pomsoft"
              size={162}
              sources={[
                '/pomsoft_dark_background.png',
                '/powered_by_pomsoft.png',
                'https://pomsoft.net/images/pomsoft_dark_background.png',
              ]}
            />
            <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: { xs: '2rem', md: '3.25rem' } }}>
              Pomsoft
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ResilientLogo
              alt="EyeSense.ai"
              size={220}
              sources={['/eyesense-logo-v2.png', '/eyesense-logo.svg']}
            />
            <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: { xs: '2rem', md: '3.25rem' } }}>
              EyeSense.ai
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={sectionTitleStyle}>Ship a USB. Install Linux. Then EyeSense.</Typography>
          <Typography
            sx={{
              color: '#e2e8f0',
              fontSize: { xs: '1.2rem', md: '2rem' },
              mt: 1.3,
              fontWeight: 500,
            }}
          >
            Feasible — and it keeps every gateway on the same S3 software channel.
          </Typography>
          <Typography
            sx={{
              color: '#cbd5e1',
              fontSize: { xs: '1rem', md: '1.25rem' },
              mt: 2,
              fontWeight: 500,
              maxWidth: 820,
              mx: 'auto',
            }}
          >
            Download the ISO, flash a 16&nbsp;GB+ stick, boot the target PC from USB. Step-by-step is on the install page.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 2.5 }}>
            <Box
              component="a"
              href="https://eyesense-downloads-435236352827.s3.us-east-1.amazonaws.com/eyesense-usb-installer.iso"
              sx={{
                color: '#082f49',
                bgcolor: '#22d3ee',
                fontWeight: 800,
                fontSize: '1.05rem',
                px: 2.5,
                py: 1.1,
                borderRadius: '10px',
                textDecoration: 'none',
              }}
            >
              Download installer ISO (~9.2 GB)
            </Box>
            <Box
              component="a"
              href="/install-gateway"
              onClick={(event) => {
                event.preventDefault();
                navigate('/install-gateway');
              }}
              sx={{
                color: '#e0f2fe',
                bgcolor: '#0ea5e9',
                fontWeight: 800,
                fontSize: '1.05rem',
                px: 2.5,
                py: 1.1,
                borderRadius: '10px',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              USB flash instructions
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            height: 4,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #22d3ee 0%, #a78bfa 100%)',
            mb: 3.5,
          }}
        />

        <Box
          component="img"
          src="/eyesense-usb-bench-hero.png"
          alt="EyeSense mini PC and USB installer on a night workbench, garden and deer beyond the window"
          sx={{
            ...wideBannerSx,
            borderRadius: '16px',
            border: '1px solid rgba(34,211,238,0.35)',
            boxShadow: '0 0 32px rgba(34,211,238,0.16)',
            mb: 3.5,
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              ...panelBase,
              position: 'relative',
              overflow: 'hidden',
              borderColor: 'rgba(34,211,238,0.6)',
              boxShadow: '0 0 24px rgba(34,211,238,0.2)',
              background:
                'radial-gradient(circle at 15% 50%, rgba(34,211,238,0.16), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.78) 0%, rgba(2,6,23,0.62) 100%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 14,
                bottom: 14,
                left: 0,
                width: '5px',
                borderRadius: '0 8px 8px 0',
                background: 'linear-gradient(180deg, rgba(34,211,238,0.95), rgba(34,211,238,0.25))',
                boxShadow: '0 0 20px rgba(34,211,238,0.45)',
              },
              ...sheen,
            }}
          >
            <Typography sx={{ color: '#22d3ee', fontSize: '2rem', fontWeight: 800, mb: 2, position: 'relative', zIndex: 1 }}>
              Why USB Beats Imaging Disks
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - Stick goes in the box. Unattended Kubuntu install. Then the published EyeSense zip.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - Software always matches S3. No golden-image SHA fighting the nightly updater.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - New SKUs (next Mini PC, next SSD size) do not require a full Clonezilla remint.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              - Field restock is a labeled stick, not a 500GB disk image on a truck.
            </Typography>
          </Box>

          <Box
            sx={{
              ...panelBase,
              position: 'relative',
              overflow: 'hidden',
              borderColor: 'rgba(196,181,253,0.65)',
              boxShadow: '0 0 24px rgba(196,181,253,0.18)',
              background:
                'radial-gradient(circle at 85% 50%, rgba(168,85,247,0.16), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.8) 0%, rgba(2,6,23,0.64) 100%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 14,
                bottom: 14,
                right: 0,
                width: '5px',
                borderRadius: '8px 0 0 8px',
                background: 'linear-gradient(180deg, rgba(168,85,247,0.95), rgba(168,85,247,0.25))',
                boxShadow: '0 0 20px rgba(168,85,247,0.45)',
              },
              ...sheen,
            }}
          >
            <Box
              component="img"
              src="/eyesense-usb-stick.png"
              alt="EyeSense-branded USB installer stick"
              sx={{
                ...wideBannerSx,
                borderRadius: '12px',
                mb: 2,
                position: 'relative',
                zIndex: 1,
              }}
            />
            <Typography sx={{ color: '#a78bfa', fontSize: '2rem', fontWeight: 800, mb: 2, position: 'relative', zIndex: 1 }}>
              What The Stick Does
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              1. UEFI boot Ubuntu / Kubuntu 24.04 autoinstall (no questions).
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              2. Late-command installs Docker, Plasma extras, and the EyeSense first-boot wizard.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              3. Copies or downloads <code style={{ color: '#67e8f9' }}>eyesense-ai-distro.zip</code> — USB payload or S3.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              4. Reboots to the owner wizard. <code style={{ color: '#67e8f9' }}>setup.sh --vpn</code> claims the hub.
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>
              5. No device token, no .env, no WireGuard identity on the stick.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: '14px',
            border: '1px solid rgba(148,163,184,0.45)',
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.82) 0%, rgba(30,41,59,0.72) 50%, rgba(2,6,23,0.82) 100%)',
            position: 'relative',
            overflow: 'hidden',
            py: { xs: 2.2, md: 2.8 },
            px: { xs: 2, md: 3.5 },
            textAlign: 'center',
            mb: 3.5,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 30% 50%, rgba(34,211,238,0.12), transparent 40%), radial-gradient(circle at 70% 50%, rgba(168,85,247,0.12), transparent 40%)',
            },
            ...sheen,
          }}
        >
          <Typography
            onClick={() => navigate('/factory/golden-image')}
            sx={{ color: '#f8fafc', fontSize: { xs: '1.35rem', md: '2.1rem' }, fontWeight: 600, lineHeight: 1.35, position: 'relative', zIndex: 1, cursor: 'pointer' }}
          >
            Use the golden image to freeze the first ten units.
          </Typography>
          <Typography sx={{ color: '#22d3ee', fontSize: { xs: '1.35rem', md: '2.1rem' }, fontWeight: 700, lineHeight: 1.35, position: 'relative', zIndex: 1 }}>
            Use the USB to stand up every unit after that.
          </Typography>
        </Box>

        <Box
          component="img"
          src="/eyesense-usb-garden-site.png"
          alt="Night garden site with EyeSense gateway under the eave and a deer beyond the fence"
          sx={{
            ...wideBannerSx,
            borderRadius: '16px',
            border: '1px solid rgba(168,85,247,0.35)',
            boxShadow: '0 0 32px rgba(168,85,247,0.14)',
            mb: 3.5,
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box sx={panelBase}>
            <Typography sx={{ color: '#67e8f9', fontSize: '1.5rem', fontWeight: 800, mb: 1.4 }}>
              QC Hardware We Design Against
            </Typography>
            <Typography sx={bulletStyle}>- ACEMAGIC K1 Mini PC</Typography>
            <Typography sx={bulletStyle}>- AMD Ryzen 4300U, 28W</Typography>
            <Typography sx={bulletStyle}>- 16GB DDR4, 512GB SSD</Typography>
            <Typography sx={bulletStyle}>- UEFI, Ethernet first-boot (no GPU required)</Typography>
            <Typography sx={bulletStyle}>- Same class as the garden / gate-house appliance</Typography>
          </Box>

          <Box sx={panelBase}>
            <Typography sx={{ color: '#a78bfa', fontSize: '1.5rem', fontWeight: 800, mb: 1.4 }}>
              Roadmap
            </Typography>
            <Typography sx={bulletStyle}>0. Done — S3 zip, wizard, bake, nightly updater.</Typography>
            <Typography sx={bulletStyle}>1. Spec — autoinstall user-data, disk layout, identity.</Typography>
            <Typography sx={bulletStyle}>2. ISO — Kubuntu 24.04 unattended + late-commands.</Typography>
            <Typography sx={bulletStyle}>3. Offline — zip + sidecar on a second USB partition.</Typography>
            <Typography sx={bulletStyle}>4. QC — three cold boots on the K1, claim, overnight green.</Typography>
            <Typography sx={bulletStyle}>5. Ship — labeled stick + QR to /install-gateway.</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.2,
            mb: 2,
            borderRadius: '12px',
            p: 1,
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.72) 0%, rgba(30,41,59,0.6) 50%, rgba(2,6,23,0.72) 100%)',
            border: '1px solid rgba(148,163,184,0.35)',
            position: 'relative',
            overflow: 'hidden',
            ...sheen,
          }}
        >
          <Chip label="Golden image (now)" sx={{ bgcolor: '#1e293b', color: '#f8fafc', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Autoinstall ISO" sx={{ bgcolor: '#22d3ee', color: '#082f49', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="S3 zip install" sx={{ bgcolor: '#818cf8', color: '#eef2ff', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Owner wizard" sx={{ bgcolor: '#a855f7', color: '#f5f3ff', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Claim on eyesense.ai" sx={{ bgcolor: '#0ea5e9', color: '#e0f2fe', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography sx={{ color: '#67e8f9', fontSize: { xs: '0.95rem', md: '1.15rem' }, opacity: 0.95 }}>
            #EyeSense #EdgeAI #IoT #ComputerVision #Linux #BuildInPublic
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default UsbInstallerRoadmap;
